<script lang="ts">
  import type { AppController, Tab } from '../../app/controller.svelte';
  import AudioTab from '../tabs/AudioTab.svelte';
  import VisualizationsTab from '../tabs/VisualizationsTab.svelte';
  import OutputsTab from '../tabs/OutputsTab.svelte';
  import PresetsTab from '../tabs/PresetsTab.svelte';
  import SceneTab from '../tabs/SceneTab.svelte';
  import SettingsTab from '../tabs/SettingsTab.svelte';
  import Icon from './Icon.svelte';

  let { ctl, oncalibrate }: { ctl: AppController; oncalibrate: () => void } = $props();
  const tabs: { id: Tab; label: string }[] = [
    { id: 'scene', label: 'Scene' },
    { id: 'visualizations', label: 'Visuals' },
    { id: 'presets', label: 'Presets' },
    { id: 'audio', label: 'Audio' },
    { id: 'outputs', label: 'Outputs' },
    { id: 'settings', label: 'Settings' },
  ];
</script>

<aside class="panel" data-testid="control-panel" aria-label="Visualizer controls">
  <header>
    <div class="head-tools">
      <button
        class="icon-btn labelled"
        class:on={ctl.panes.live}
        aria-pressed={ctl.panes.live}
        data-testid="toggle-live-pane"
        title={`Live pane: tempo, tap, beat${ctl.keyHint('ui.livePane')}`}
        aria-label="Live pane"
        onclick={() => ctl.setPane('live', !ctl.panes.live)}><Icon name="pulse" /> Live</button
      >
      <button
        class="icon-btn labelled"
        class:on={ctl.panes.strip}
        aria-pressed={ctl.panes.strip}
        data-testid="toggle-strip"
        title={`Favorites & banks strip${ctl.keyHint('ui.strip')}`}
        aria-label="Favorites and banks strip"
        onclick={() => ctl.setPane('strip', !ctl.panes.strip)}><Icon name="strip" /> Strip</button
      >
      <button
        class="icon-btn pvw"
        class:on={ctl.previewMode}
        data-tour="preview"
        data-testid="preview-toggle"
        aria-pressed={ctl.previewMode}
        title={`Preview mode: prepare here, then take it live${ctl.keyHint('preview.toggle')}`}
        aria-label="Preview mode"
        onclick={() => ctl.setPreviewMode(!ctl.previewMode)}>PVW</button
      >
      <span class="grow"></span>
      <button
        class="icon-btn"
        data-testid="fullscreen"
        title={`Full screen${ctl.keyHint('ui.fullscreen')}. Hides the browser's bars, including the “sharing this tab” bar.`}
        aria-label="Full screen"
        onclick={() => ctl.runAction('ui.fullscreen', undefined)}><Icon name="fullscreen" /></button
      >
      <button
        class="icon-btn"
        data-tour="help"
        title="Help and keyboard shortcuts (?)"
        aria-label="Help and keyboard shortcuts"
        data-testid="open-help"
        onclick={() => (ctl.helpOpen = true)}>?</button
      >
      <button
        class="icon-btn"
        data-tour="performance"
        title={`Performance mode${ctl.keyHint('ui.performance')}`}
        aria-label="Performance mode"
        onclick={() => ctl.setPerformance(true)}><Icon name="stage" /></button
      >
      <button
        class="icon-btn"
        title={`Hide controls${ctl.keyHint('ui.toggleControls')}`}
        aria-label="Hide controls"
        onclick={() => (ctl.panelOpen = false)}><Icon name="close" /></button
      >
    </div>
    <div class="tabs" role="tablist" aria-label="Sections" data-tour="tabs">
      {#each tabs as t (t.id)}
        <button
          role="tab"
          aria-selected={ctl.tab === t.id}
          class:active={ctl.tab === t.id}
          data-testid={`tab-${t.id}`}
          onclick={() => (ctl.tab = t.id)}>{t.label}</button
        >
      {/each}
    </div>
  </header>
  <div class="body" role="tabpanel">
    {#if ctl.tab === 'scene'}<SceneTab {ctl} />
    {:else if ctl.tab === 'visualizations'}<VisualizationsTab {ctl} />
    {:else if ctl.tab === 'presets'}<PresetsTab {ctl} />
    {:else if ctl.tab === 'audio'}<AudioTab {ctl} {oncalibrate} />
    {:else if ctl.tab === 'outputs'}<OutputsTab {ctl} />
    {:else}<SettingsTab {ctl} />{/if}
  </div>
</aside>

<style>
  .head-tools {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .labelled {
    width: auto;
    padding: 0 7px;
    font-size: 11.5px;
  }
  .pvw {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    width: auto;
    padding: 0 6px;
  }
  .pvw.on {
    background: rgba(75, 227, 160, 0.9);
    color: #04140c;
  }
  .panel {
    position: absolute;
    top: 12px;
    right: 12px;
    bottom: 12px;
    width: 436px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    background: var(--panel);
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }
  @media (max-width: 640px) {
    .panel {
      top: auto;
      left: 8px;
      right: 8px;
      bottom: 8px;
      width: auto;
      height: 62vh;
      padding-bottom: env(safe-area-inset-bottom);
    }
  }
  header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    padding: 8px 8px 0 10px;
    border-bottom: 1px solid var(--border);
  }
  .tabs {
    display: flex;
    gap: 2px;
    overflow-x: auto;
    scrollbar-width: none;
    flex: 1;
  }
  .tabs button {
    border: none;
    background: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    padding: 8px 4px 9px;
    font-size: 12.5px;
    color: var(--muted);
    white-space: nowrap;
  }
  .tabs button.active {
    color: var(--text);
    border-bottom-color: var(--accent);
  }
  .head-tools :global(.icon-btn) {
    padding: 3px 4px;
  }
  .body {
    flex: 1;
    overflow-y: auto;
    padding: 0 14px 16px;
    scrollbar-width: thin;
  }
</style>
